"use client";

/**
 * Страница контактов (клиент): переключение города БЕЗ изменения URL.
 * Слева — карта + контакты выбранного города и кнопки «Обратный звонок» /
 * «Написать нам»; справа — список городов. Формы открываются в модалке и шлют
 * заявку в /api/v1/feedback по station — бэкенд сам роутит на почту и telegram
 * выбранного города.
 */

import Img from '@/components/Img';
import { useState, useEffect } from "react";
import Image from "next/image";
import PhoneInput from "@/components/PhoneInput";
import type { RentSiteCity } from "@/lib/fleet-server";
import { useLocale } from "@/lib/locale-context";
import { pickLocalized } from "@/lib/locale-utils";
import { track } from "@/lib/metrika";

function buildMapSrc(lat: number, lng: number, provider: "yandex" | "google", locale: string): string {
  const en = locale === "en";
  return provider === "google"
    ? `https://www.google.com/maps?q=${lat},${lng}&z=15&output=embed${en ? "&hl=en" : ""}`
    : `https://yandex.ru/map-widget/v1/?ll=${lng}%2C${lat}&z=15&pt=${lng}%2C${lat}%2Cpm2rdm&lang=${en ? "en_US" : "ru_RU"}`;
}

function extLink(u: string): string { return /^https?:\/\//.test(u) ? u : `https://${u}`; }

// Соцсеть/мессенджер — белая иконка на фирменном кружке (как в футере).
// full: иконка-бейдж со своим фоном (MAX) — без перекраски.
function Messenger({ href, label, icon, bg, full, onClick }: { href: string; label: string; icon: string; bg: string; full?: boolean; onClick?: () => void }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label} title={label} onClick={onClick}
      className="flex size-11 items-center justify-center overflow-hidden rounded-full transition hover:-translate-y-0.5 hover:opacity-90"
      style={{ background: bg }}>
      {full
        ? <Img src={icon} alt={label} className="size-11 object-contain" />
        : <Img src={icon} alt={label} className="size-6" style={{ filter: "brightness(0) invert(1)" }} />}
    </a>
  );
}

const Icon = {
  phone: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0039A6" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.18 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.33 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" /></svg>,
  mail: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0039A6" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>,
  pin: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0039A6" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>,
  clock: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0039A6" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>,
};

export default function ContactsClient({ cities }: { cities: RentSiteCity[] }) {
  const { t, locale } = useLocale();
  const [active, setActive] = useState(0);
  const [officeIdx, setOfficeIdx] = useState(0); // какой офис показан на карте
  const [modal, setModal] = useState<null | "callback" | "message">(null);

  // Переход с плитки города на главной (/contacts#slug) — сразу выбираем город.
  useEffect(() => {
    const slug = decodeURIComponent((typeof window !== "undefined" ? window.location.hash : "").replace(/^#/, ""));
    if (!slug) return;
    const i = cities.findIndex((c) => c.slug === slug);
    if (i >= 0) setActive(i);
  }, [cities]);

  // При смене города сбрасываем выбранный офис.
  useEffect(() => { setOfficeIdx(0); }, [active]);

  const city = cities[active];
  if (!city) return null;
  const s = city.settings || {};
  const provider = s.map_provider || "yandex";
  const offices = s.offices || [];
  const hasCoords = (o?: { lat?: number; lng?: number }) => !!o && o.lat != null && o.lng != null;
  const multiOffice = offices.length > 1;
  // Выбранный офис (адрес + часы + карта). Контакты телефон/почта/мессенджеры — общие по городу.
  const curOffice = offices[officeIdx] || offices[0];
  const officeLabel = pickLocalized(curOffice?.name, curOffice?.name_en, locale) || t("contacts.office", { n: String(officeIdx + 1) });
  // Часы: сперва офиса (лок.), иначе дефолт города (лок.). EN берёт *_en, иначе RU.
  const officeHours = pickLocalized(curOffice?.working_hours, curOffice?.working_hours_en, locale) || pickLocalized(s.working_hours, s.working_hours_en, locale);
  const officeAddress = pickLocalized(curOffice?.address, curOffice?.address_en, locale);
  const mapPt = hasCoords(curOffice) ? { lat: curOffice!.lat!, lng: curOffice!.lng! } : undefined;
  const map = s.map_enabled && mapPt ? buildMapSrc(mapPt.lat, mapPt.lng, provider, locale) : null;

  return (
    <div className="mx-auto max-w-[1200px] px-5 sm:px-8 xl:px-0">
      <h1 className="mt-8 text-[40px] font-bold leading-tight text-black sm:text-5xl">{t("nav.contacts")}</h1>

      <div className="mt-8 flex flex-col gap-6 lg:flex-row lg:items-stretch">
        {/* ЛЕВО: выбор города — на всю высоту правого блока (контакты + карта) */}
        <aside className="w-full lg:w-[280px] lg:flex-shrink-0">
          <div className="flex flex-col overflow-hidden rounded-2xl border border-[#E2E8F4] bg-white lg:h-full">
            <div className="shrink-0 border-b border-[#EEF2F9] px-4 py-3 text-[13px] font-semibold uppercase tracking-wide text-[#8791AD]">{t("form.city")}</div>
            <div className="flex max-h-[420px] flex-1 flex-col gap-0.5 overflow-y-auto p-2 lg:max-h-none lg:min-h-0">
              {cities.map((c, i) => {
                const coffices = c.settings?.offices || [];
                const isActive = i === active;
                return (
                  <div key={c.slug}>
                    <button
                      onClick={() => { setActive(i); setOfficeIdx(0); }}
                      className={`flex w-full items-center justify-between rounded-xl px-3 py-[11px] text-left text-[16px] transition ${isActive ? "bg-primary font-semibold text-white" : "text-text hover:bg-[#F6FAFF]"}`}
                    >
                      {pickLocalized(c.name, c.name_en, locale)}
                      {isActive && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>}
                    </button>
                    {isActive && coffices.length > 1 && (
                      <div className="mb-1 ml-3 mt-1 flex flex-col gap-0.5 border-l-2 border-primary/20 pl-2">
                        {coffices.map((o, oi) => (
                          <button
                            key={oi}
                            onClick={() => setOfficeIdx(oi)}
                            className={`rounded-lg px-3 py-2 text-left text-[14px] transition ${oi === officeIdx ? "bg-primary/10 font-semibold text-primary" : "text-[#5A6178] hover:bg-[#F6FAFF]"}`}
                          >
                            {pickLocalized(o.name, o.name_en, locale) || t("contacts.office", { n: String(oi + 1) })}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </aside>

        {/* ПРАВО: контакты + карта */}
        <div className="flex flex-1 flex-col gap-5">
          <div className="flex flex-col overflow-hidden rounded-2xl border border-[#E2E8F4] bg-white shadow-[0_4px_24px_rgba(20,29,69,0.06)] lg:flex-row">
            {/* Баннер: фото города + название (слева, на всю высоту данных) */}
            <div className="relative h-[220px] shrink-0 bg-primary-soft lg:h-auto lg:w-[44%] lg:min-h-[380px]">
              {city.image_url && (
                <Image key={city.slug} src={city.image_url} alt={city.name} fill sizes="(max-width:1024px) 100vw, 460px" className="object-cover" style={{ animation: "rsFade 0.5s ease" }} />
              )}
              <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(180deg, rgba(20,29,69,0.06) 35%, rgba(20,29,69,0.66) 100%)" }} />
              <h2 className="absolute bottom-5 left-7 text-[32px] font-bold leading-none text-white" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.35)" }}>{locale === "en" && (city as { name_en?: string }).name_en ? (city as { name_en?: string }).name_en : city.name}</h2>
            </div>

            {/* Контакты + кнопки (справа) */}
            <div className="flex flex-1 flex-col gap-4 p-7">
              {s.phone && (
                <a href={`tel:${s.phone.replace(/[^+\d]/g, "")}`} onClick={() => track("phone_click", { city: city.slug })} className="group flex items-center gap-4">
                  <span className="flex size-11 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">{Icon.phone}</span>
                  <span className="text-[19px] font-semibold text-[#141D45] transition group-hover:text-primary">{s.phone}</span>
                </a>
              )}
              {s.email && (
                <a href={`mailto:${s.email}`} onClick={() => track("email_click", { city: city.slug })} className="group flex items-center gap-4">
                  <span className="flex size-11 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">{Icon.mail}</span>
                  <span className="text-[16px] text-[#141D45] transition group-hover:text-primary">{s.email}</span>
                </a>
              )}
              {officeAddress && (
                <div className="flex items-start gap-4">
                  <span className="flex size-11 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">{Icon.pin}</span>
                  <span className="flex flex-col gap-0.5 pt-1.5">
                    {multiOffice && <span className="text-[15px] font-semibold leading-tight text-[#141D45]">{officeLabel}</span>}
                    <span className="text-[16px] leading-relaxed text-[#141D45]" style={{ whiteSpace: "pre-line" }}>{officeAddress}</span>
                  </span>
                </div>
              )}
              {officeHours && (
                <div className="flex items-center gap-4">
                  <span className="flex size-11 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">{Icon.clock}</span>
                  <span className="text-[16px] text-[#141D45]">{officeHours}</span>
                </div>
              )}
              {(s.soc_wa || s.soc_tg || s.soc_max) && (
                <div className="flex items-center gap-3 pt-1">
                  {s.soc_wa && <Messenger href={extLink(s.soc_wa)} label="WhatsApp" icon="/icons/social/wa.svg" bg="#25d366" onClick={() => track("whatsapp_click", { city: city.slug })} />}
                  {s.soc_tg && <Messenger href={extLink(s.soc_tg)} label="Telegram" icon="/icons/social/tg.svg" bg="#26a5e4" onClick={() => track("telegram_click", { city: city.slug })} />}
                  {s.soc_max && <Messenger href={extLink(s.soc_max)} label="MAX" icon="/icons/social/max.svg" bg="transparent" full onClick={() => track("social_click", { city: city.slug })} />}
                </div>
              )}
              {(() => {
                // Отзывы ТОЛЬКО текущего филиала (выбранного офиса curOffice):
                // его Яндекс.Карты и 2ГИС. Других офисов не показываем.
                const yaUrl = curOffice?.yandex_url;
                const tgUrl = curOffice?.twogis_url;
                if (!yaUrl && !tgUrl) return null;
                const link = (url: string, logo: string, name: string) => (
                  <a href={url} target="_blank" rel="noopener noreferrer" aria-label={name} title={name}
                     onClick={() => track("review_source_click", { city: city.slug })}
                     className="inline-flex items-center justify-center rounded-xl border border-[#E2E6EE] bg-white px-4 py-2.5 transition hover:border-primary">
                    <Img src={logo} alt={name} className="h-6 w-auto object-contain" />
                  </a>
                );
                return (
                  <div className="flex flex-col gap-2 pt-1">
                    <span className="text-[14px] font-semibold text-[#5A6178]">{t("contacts.reviews")}</span>
                    <div className="flex flex-wrap gap-2.5">
                      {yaUrl && link(yaUrl, "/icons/review/yandex.png", "Яндекс.Карты")}
                      {tgUrl && link(tgUrl, "/icons/review/2gis.png", "2ГИС")}
                    </div>
                  </div>
                );
              })()}
              {!s.phone && !s.email && !offices.some((o) => o.address) && (
                <p className="text-[15px] text-text-muted">{t("contacts.notFilled")}</p>
              )}
              <div className="mt-2 flex flex-wrap gap-3">
                <button onClick={() => { track("callback_open", { city: city.slug }); setModal("callback"); }} className="rounded-xl bg-primary px-6 py-3.5 text-[15px] font-semibold text-white transition hover:bg-primary-hover">
                  {t("contacts.callback")}
                </button>
                <button onClick={() => { track("contacts_message_open", { city: city.slug }); setModal("message"); }} className="rounded-xl border border-[#141D45] bg-white px-6 py-3.5 text-[15px] font-semibold text-[#141D45] transition hover:bg-[#141D45] hover:text-white">
                  {t("contacts.writeUs")}
                </button>
              </div>
            </div>
          </div>

          {map && mapPt && (
            <div className="flex flex-col gap-4">
              {multiOffice && (
                <div className="flex items-center gap-2 text-[14px] text-[#5A6178]">
                  <span className="size-2 rounded-full bg-primary" />
                  {t("contacts.onMap")}&nbsp;<span className="font-semibold text-[#141D45]">{officeLabel}</span>
                </div>
              )}
              <div className="overflow-hidden rounded-2xl border border-[#E2E8F4] shadow-[0_4px_24px_rgba(20,29,69,0.06)]">
                <iframe key={map} src={map} title={t("contacts.mapTitle", { city: (locale === "en" && (city as { name_en?: string }).name_en ? (city as { name_en?: string }).name_en! : city.name) })} className="h-[460px] w-full border-0" loading="lazy" allowFullScreen />
              </div>
              <div className="flex justify-center">
                <RouteButton lat={mapPt.lat} lng={mapPt.lng} provider={provider} />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="h-12" />

      {modal && (
        <FeedbackModal
          mode={modal}
          city={city}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

/* Кнопка «Проложить маршрут» — открывает тот же навигатор, что и карта города:
 * Яндекс.Карты (rtext=~до точки) либо Google Maps (dir). На мобильных обе ссылки
 * сами поднимают приложение, если оно установлено. */
function RouteButton({ lat, lng, provider }: { lat: number; lng: number; provider: "yandex" | "google" }) {
  const { t } = useLocale();
  const href = provider === "google"
    ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
    : `https://yandex.ru/maps/?rtext=~${lat}%2C${lng}&rtt=auto`;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" onClick={() => track("route_click")} className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-[15px] font-semibold text-white shadow-[0_4px_14px_rgba(0,57,166,0.25)] transition hover:bg-primary-hover">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11" /></svg>
      {t("contacts.route")}
    </a>
  );
}

/* ───────────── Модалка с формой ───────────── */
function FeedbackModal({ mode, city, onClose }: { mode: "callback" | "message"; city: RentSiteCity; onClose: () => void }) {
  const { t } = useLocale();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneDial, setPhoneDial] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreePersonal, setAgreePersonal] = useState(false);
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
  const isCallback = mode === "callback";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !phone.trim() || !agreePrivacy || !agreePersonal) { setState("error"); return; }
    setState("sending");
    const fullPhone = `${phoneDial} ${phone}`.trim();
    try {
      const res = await fetch("/api/v1/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          station: city.station,
          place: isCallback ? "CALLBACK" : "CONTACTS",
          clientName: name,
          clientPhone: fullPhone,
          clientEmail: email,
          comment: isCallback ? `Обратный звонок — ${city.name}` : message,
          site: "autopilot",
        }),
      });
      if (res.ok) track(isCallback ? "callback_submit" : "contacts_message_submit", { city: city.slug });
      setState(res.ok ? "done" : "done");
    } catch { setState("done"); }
  }

  const inp = "w-full rounded-lg border border-[#C9D3E6] bg-white px-4 py-3 text-[16px] text-black outline-none focus:border-primary";
  return (
    <div onClick={onClose} className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-[440px] rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-[22px] font-bold text-[#141D45]">{isCallback ? t("contacts.callback") : t("contacts.writeUs")}</h3>
          <button onClick={onClose} aria-label={t("common.close")} className="text-[#8791AD] transition hover:text-black">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <p className="mb-4 text-[14px] text-text-muted">{city.name} · {isCallback ? t("contacts.callbackHint") : t("contacts.messageHint")}</p>

        {state === "done" ? (
          <div className="flex flex-col gap-2 rounded-xl bg-[#F6FAFF] p-5">
            <span className="text-[18px] font-semibold text-[#141D45]">{t("contacts.thanks")}</span>
            <span className="text-[15px] text-text-muted">{t("contacts.sentMsg")}</span>
            <button onClick={onClose} className="mt-2 self-start rounded-lg bg-primary px-5 py-2.5 text-[14px] font-semibold text-white">{t("common.close")}</button>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("contacts.namePh")} className={inp} />
            <PhoneInput value={phone} onChange={(p, d) => { setPhone(p); setPhoneDial(d); }} required height={50} fontSize={16} />
            {!isCallback && (
              <>
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={inp} />
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t("form.message")} rows={3} className={`${inp} resize-y`} />
              </>
            )}
            <label className="flex items-start gap-2 text-[13px] text-[#5A6178]">
              <input type="checkbox" checked={agreePrivacy} onChange={(e) => setAgreePrivacy(e.target.checked)} className="mt-0.5 accent-primary" />
              <span>{t("consent.privacyPrefix")} <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline">{t("consent.privacy")}</a></span>
            </label>
            <label className="flex items-start gap-2 text-[13px] text-[#5A6178]">
              <input type="checkbox" checked={agreePersonal} onChange={(e) => setAgreePersonal(e.target.checked)} className="mt-0.5 accent-primary" />
              <span>{t("consent.personalPrefix")} <a href="/legal/personal-data" target="_blank" rel="noopener noreferrer" className="text-primary underline">{t("consent.personalData")}</a></span>
            </label>
            {state === "error" && <span className="text-[13px] text-danger">{t("contacts.formError")}</span>}
            <button type="submit" disabled={state === "sending"} className="mt-1 rounded-xl bg-primary px-6 py-3 text-[15px] font-semibold text-white transition hover:bg-primary-hover disabled:opacity-60">
              {state === "sending" ? t("form.sending") : (isCallback ? t("contacts.orderCall") : t("form.send"))}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
